import * as THREE from 'three';
import type { CollisionWorld } from '../shared/physics';
import type { Vec3 } from '../shared/types';
import {
  DEFAULT_VIEW_GRID,
  VIEW_GRID_WINDOW,
  localToWorldXZ,
  normalizeViewGrid,
  worldToLocalXZ,
  type ViewGridState,
} from '../shared/view-grid';

export const VIEW_GRID_STORAGE_KEY = 'mapeditor:view-grid';
const GRID_COLOR = 0x3ddc6a;

export class ViewGridOverlay {
  readonly group = new THREE.Group();
  private readonly lines: THREE.LineSegments;
  private state: ViewGridState;
  private collision: CollisionWorld | null = null;

  constructor() {
    this.state = loadViewGrid();
    this.lines = new THREE.LineSegments(
      buildGridGeometry(this.state.snap),
      new THREE.LineBasicMaterial({
        color: GRID_COLOR,
        transparent: true,
        opacity: 0.72,
        depthWrite: false,
        toneMapped: false,
      })
    );
    this.lines.frustumCulled = false;
    this.group.add(this.lines);
    this.group.name = 'view-grid';
    this.applyVisual();
  }

  get current(): ViewGridState {
    return { ...this.state };
  }

  attachCollision(world: CollisionWorld): void {
    this.collision = world;
    this.applyCollision();
  }

  setState(next: Partial<ViewGridState>): void {
    const previousSnap = this.state.snap;
    this.state = normalizeViewGrid({ ...this.state, ...next });
    saveViewGrid(this.state);
    if (this.state.snap !== previousSnap) {
      this.lines.geometry.dispose();
      this.lines.geometry = buildGridGeometry(this.state.snap);
    }
    this.applyVisual();
    this.applyCollision();
  }

  follow(position: Vec3): void {
    if (!this.state.visible) return;
    const step = this.state.snap;
    const local = worldToLocalXZ(position.x, position.z, this.state.yaw);
    const snapped = {
      x: Math.round(local.x / step) * step,
      z: Math.round(local.z / step) * step,
    };
    const world = localToWorldXZ(snapped.x, snapped.z, this.state.yaw);
    this.group.position.set(world.x, this.state.y, world.z);
    this.group.rotation.set(0, (this.state.yaw * Math.PI) / 180, 0);
  }

  dispose(): void {
    this.lines.geometry.dispose();
    (this.lines.material as THREE.Material).dispose();
    if (this.collision) this.collision.oneWayFloorY = null;
  }

  private applyVisual(): void {
    this.group.visible = this.state.visible;
    this.group.position.y = this.state.y;
    this.group.rotation.y = (this.state.yaw * Math.PI) / 180;
  }

  private applyCollision(): void {
    if (!this.collision) return;
    this.collision.oneWayFloorY = this.state.visible ? this.state.y : null;
  }
}

function buildGridGeometry(snap: number): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const half = VIEW_GRID_WINDOW / 2;
  const positions: number[] = [];
  for (let index = -half; index <= half + 1e-9; index += snap) {
    positions.push(index, 0, -half, index, 0, half);
    positions.push(-half, 0, index, half, 0, index);
  }
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  return geometry;
}

export function loadViewGrid(): ViewGridState {
  try {
    const raw = window.localStorage.getItem(VIEW_GRID_STORAGE_KEY);
    return raw ? normalizeViewGrid(JSON.parse(raw) as unknown) : { ...DEFAULT_VIEW_GRID };
  } catch {
    return { ...DEFAULT_VIEW_GRID };
  }
}

export function saveViewGrid(state: ViewGridState): void {
  window.localStorage.setItem(VIEW_GRID_STORAGE_KEY, JSON.stringify(state));
}
